import React, { useState, useEffect } from 'react';
import { Form, InputGroup, Alert, Card, Button, Table } from 'react-bootstrap';
import FinancialDetails from './FinancialDetails';
import { RPWDataReader, getDataReader } from '../../livedataFuncs';
import { useAppSelector } from '../../redux/hooks';
import { lddStateObject } from '../../redux/reducer';
import { runInnerFunction } from '../../compatManager';

const SolunoExport: React.FC = () => {
    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    let DataReaderObj: RPWDataReader;
    DataReaderObj = getDataReader(livedataRootContainer.dataRoot);
    const fileNumber = DataReaderObj.getFileNumber();
    const [userToken, setUserToken] = useState<string>('');
    const [orgId, setOrgId] = useState<string>('');
    const [isFileLinked, setIsFileLinked] = useState<boolean>(false);
    const [searchInput, setSearchInput] = useState<string>(fileNumber);
    const [searchResult, setSearchResult] = useState<any>(null);
    const [linkedCasefileId, setLinkedCasefileId] = useState<string>('');
    const [staticPreLoad, setStaticPreLoad] = useState<any[]>([]);
    const [isComponentMounted, setIsComponentMounted] = useState(false);
    const [defaultTaskId, setDefaultTaskId] = useState<string | null>(null);
    const [alertMessage, setAlertMessage] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [validCredentialsAndLogin, setValidCredentialsAndLogin] = useState<boolean>(true);
    const [validOrganization, setValidOrganization] = useState<boolean>(true);

    let orgName = '';
    let username = '';
    let password = '';

    const solunoDetails = DataReaderObj.getSolunoDetailsIfPresent();
    if (solunoDetails.org && solunoDetails.email && solunoDetails.password) {
        orgName = solunoDetails.org;
        username = solunoDetails.email; 
        password = solunoDetails.password;
    }
    const hasValidCredentials = orgName !== '' && username !== '' && password !== '';

    const generateDeviceToken = (): string => {
        // Format: 8-4-4-4-12 characters
        const segments = [8, 4, 4, 4, 12];
        const chars = '0123456789abcdef';
        
        return segments
            .map(length => {
                let segment = '';
                for (let i = 0; i < length; i++) {
                    segment += chars[Math.floor(Math.random() * chars.length)];
                }
                return segment;
            })
            .join('-');
    };
    
    const customChallenge = 'Devlos Basic Challenge:Blade';
    const customDeviceToken = generateDeviceToken();
    // const customDeviceToken = '49292443-c035-4541-9206-554dd4335bfc';
    const customPartnerId = 'fad5b5bd-5644-4f1b-92c3-7e955faa4f1e'; 

    const headers = {
        'custom-challenge': customChallenge,
        'custom-devicetoken': customDeviceToken,
        'custom-partnerid': customPartnerId,
        'Content-Type': 'application/json'
    };

    useEffect(() => {
        setIsComponentMounted(true);
    }, []);

    useEffect(() => {
        if (!isComponentMounted) return;

        const loadData = async () => {
            setIsLoading(true);
            try {
                DataReaderObj.exportSolunoXSLT();
                await new Promise(resolve => setTimeout(resolve, 2000));
                const token = await registerLogon();
                if (token === "Invalid"){
                    setValidCredentialsAndLogin(false);
                    return;
                } else {
                    setValidCredentialsAndLogin(true);
                }
                setUserToken(token);
                const orgIdVal = await fetchOrganizations(token, orgName);
                if (!orgIdVal) {
                    setValidOrganization(false);
                    return;
                } else {
                    setValidOrganization(true);
                    setOrgId(orgIdVal);
                }
                
                // Remove fetchCaseFiles call here since we'll search on demand
                const staticPL = await fetchStaticPreLoad(token, orgIdVal);
                setStaticPreLoad(staticPL);

                // Initial search if there's a file number
                if (searchInput) {
                    await handleSearch(token, orgIdVal);
                }
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [isComponentMounted]);

    const handleSearchInputChange = (input: string) => {
        setSearchInput(input);
    };

    const handleSearch = async (token: string, orgId: string) => {
        setIsLoading(true);
        try {
            const response = await fetch(`https://cloud.soluno.legal/api/data/Casefiles?search=${encodeURIComponent(searchInput)}&count=40&incDeleted=false&incActive=true`, {
                method: 'GET',
                headers: {
                    ...headers,
                    'Custom-UserToken': token,
                    'Custom-OrgId': orgId
                }
            }).catch(() => {
                // Silently handle network errors
                return null;
            });

            if (!response || !response.ok) {
                setSearchResult(null);
                setAlertMessage('Soluno matter not found, please enter the Soluno matter name above and click search');
                setIsLoading(false);
                return;
            }

            const data = await response.json();
            if (data && data.length > 0) {
                setSearchResult(data[0]);
                setAlertMessage('');
            } else {
                setSearchResult(null);
                setAlertMessage('Soluno matter not found, please enter the Soluno matter name above and click search');
            }
        } catch (error) {
            // This catch block should now only trigger for unexpected errors
            setSearchResult(null);
            setAlertMessage('Soluno matter not found, please enter the Soluno matter name above and click search');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileSelect = async (file: any) => {
        setSearchResult(null);
        setSearchInput(file.CasefileNumber);
        setLinkedCasefileId(file.Id);
        setIsFileLinked(true);

        // Fetch the full casefile to get its DefaultTaskId (used as preferred TaskId for fee exports).
        // See "SOL-API Starting Guide" PDF, p.6 — GET /api/data/Casefiles?id={guid} returns DefaultTaskId.
        try {
            const response = await fetch(`https://cloud.soluno.legal/api/data/Casefiles?id=${file.Id}`, {
                headers: {
                    ...headers,
                    'Custom-UserToken': userToken,
                    'Custom-OrgId': orgId
                }
            });
            if (response.ok) {
                const casefile = await response.json();
                setDefaultTaskId(casefile.DefaultTaskId ?? null);
            }
        } catch (error) {
            console.warn('[Soluno Export] Failed to fetch casefile DefaultTaskId — fee exports will fall back to "bw" task or first available task:', error);
        }
    };

    const handleUnlink = () => {
        alert('Unlinking the Soluno matter in this Realti file will not clear the data you have already exported to Soluno. Please manage the matter in Soluno to remove any exported information.');
            setSearchInput('');
            setLinkedCasefileId('');
            setIsFileLinked(false);
    };

    const registerLogon = async () => {
        try {
            const loginBody = {
                DeviceId: customDeviceToken,
                UserToken: userToken,
                UserName: username,
                Password: password,
                RememberMe: true,
                ForceLogout: true
            };

            const response = await fetch('https://cloud.soluno.legal/api/startup/Logon/RegisterLogon', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(loginBody)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.UserToken;
        } catch (error) {
            console.error('Error during login:', error);
            throw error;
        }
    };

    const fetchOrganizations = async (token: string, orgName: string) => {
        try {
            const userOrgBody = {
                DeviceId: customDeviceToken,
                UserToken: token,
            };
            const response = await fetch('https://cloud.soluno.legal/api/startup/Logon/UserOrganizations', {
                method: 'POST',
                headers: {
                    ...headers,
                    'Custom-UserToken': token
                },
                body: JSON.stringify(userOrgBody)
            });

            const data = await response.json();
            const organization = data.Organizations.find(org => org.DisplayName === orgName);
            return organization ? organization.OrgId : null;
        } catch (error) {
            console.error('Error fetching organizations:', error);
            return null;
        }
    };

    const fetchStaticPreLoad = async (token: string, orgId: string) => {
        try {
            const response = await fetch('https://cloud.soluno.legal/api/data/StaticPreLoad', {
                method: 'GET',
                headers: {
                    ...headers,
                    'Custom-UserToken': token,
                    'Custom-OrgId': orgId
                }
            });
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching static pre-load:', error);
            return [];
        }
    };

    return (
        <>
            <Card className="card-list">
                <Card.Header>
                    <h2 >Soluno</h2>
                    <InputGroup className="search-bar">
                        <InputGroup.Text>
                            <i style={{ cursor: "default" }} className="fa-duotone fa-magnifying-glass fa-indicator"/>
                        </InputGroup.Text>

                        <Form.Control 
                            type="text"
                            placeholder={hasValidCredentials ? "Enter Case File Number" : "Please add Soluno credentials to continue"} 
                            value={searchInput}
                            onChange={(e) => handleSearchInputChange(e.target.value)}
                            readOnly={isFileLinked}
                            disabled={!hasValidCredentials}
                        />

                        {isFileLinked ? (
                            <Button 
                                variant="outline-secondary"
                                onClick={handleUnlink}
                                title="Unlink File"
                            >
                                <i className="fa-solid fa-xmark"/>
                            </Button>
                        ) : (
                            <>
                                <Button 
                                    variant="primary"
                                    onClick={() => handleSearch(userToken, orgId)}
                                    disabled={!hasValidCredentials || isLoading}
                                    title={!hasValidCredentials ? "Please configure Soluno credentials (Organization, Email, and Password) to enable search" : ""}
                                >
                                    {isLoading ? (
                                        <>
                                            <i className="fa-solid fa-spinner fa-spin me-2"/>
                                            Loading...
                                        </>
                                    ) : (
                                        'Search'
                                    )}
                                </Button>
                            </>
                        )}
                    </InputGroup>
                    {!isFileLinked && <Button
                        className="btn-link"
                        onClick={() => runInnerFunction('openZoho', 'articles/soluno-accounting-integration')}
                        title="User Guide"
                        style={{ 
                            cursor: 'pointer',
                            border: 'none',
                            background: 'none',
                            padding: '0',
                            margin: '0',
                            textDecoration: 'none'
                        }}
                    >
                        <i className="fa-solid fa-book"/> User Guide
                    </Button>}
                </Card.Header>
                
                {!hasValidCredentials ? (
                    <div className="alert-area">
                        <Alert variant="warning" role="alert">
                            <b>Credentials not found.</b> Please add Soluno credentials in User Settings to proceeed.
                        </Alert>
                    </div>
                ) : !validCredentialsAndLogin ? (
                    <div className="alert-area">
                        <Alert variant="warning" role="alert">
                            <b>Your Soluno Credentials are incorrect.</b> Please verify your information in User Settings.
                        </Alert>
                    </div>
                ) : !validOrganization ? (
                    <div className="alert-area">
                        <Alert variant="warning" role="alert">
                            <b>Organization not found.</b> Please verify your organization in User Settings.
                        </Alert>
                    </div>
                ) : (
                    alertMessage ? (
                        <div className="alert-area">
                            <Alert variant="warning" role="alert">
                                <b>Casefile number not found.</b> Enter an existing Casefile number above and click 'Search'.
                            </Alert>
                        </div>
                    ): !isFileLinked &&(
                        <div className="alert-area">
                            <Alert variant="primary" role="alert">
                                <b>Select a Casefile</b> to export data to.
                            </Alert>
                        </div>
                    )
                )}

                {searchResult && (
                    <Card.Body style={{minHeight:'300px'}}>
                        <Table hover>
                            <thead>
                                <tr>
                                    <th>Casefile Number</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr 
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleFileSelect(searchResult)}
                                >
                                    <td>{searchResult.CasefileNumber}</td>
                                </tr>
                            </tbody>
                        </Table>
                    </Card.Body>
                )}
            </Card>

            {isFileLinked && (
                <>
                    <FinancialDetails
                        staticPreLoad={staticPreLoad}
                        headers={{
                            ...headers,
                            'Custom-UserToken': userToken,
                            'Custom-OrgId': orgId
                        }}
                        linkedCasefileId={linkedCasefileId}
                        casefileNumber={searchInput}
                        defaultTaskId={defaultTaskId}
                    />
                </>
            )}
        </>
    );
};

export default SolunoExport;